import React from 'react';
import { PeersonaDisplayMessage } from '../../contexts/peersona-context';
import { PeersonaHandoffCard } from './peersona-handoff-card';
import { PeersonaReferralContent } from './peersona-referral-content';
import { PeersonaSafetyCard } from './peersona-safety-card';
import {
  PEERSONA_BUBBLE_ASSISTANT_BG,
  PEERSONA_BUBBLE_ASSISTANT_TEXT,
  PEERSONA_BUBBLE_USER_BG,
  PEERSONA_BUBBLE_USER_TEXT,
  PEERSONA_TEXT_MUTED,
} from './peersona-styles';
import { formatCompactRelativeTime } from '../../utils/relative-time';

interface Props {
  message: PeersonaDisplayMessage;
  peerName: string;
  /** Reserved for future per-bubble avatar variants; unused today. */
  peerAvatar?: string | null;
}

/**
 * Peersona conversation bubble. User bubbles are dark gray on the right
 * with a 4px bottom-right corner; assistant bubbles are Kindly blue on
 * the left with a 4px bottom-left corner. No per-bubble avatar — the
 * persistent hero anchors the conversation. Assistant meta line uses
 * the full-width middle dot ("・") to match the mock typography.
 */
const USER_BUBBLE_RADII = 'rounded-tl-[20px] rounded-tr-[20px] rounded-bl-[20px] rounded-br-[4px]';
const ASSISTANT_BUBBLE_RADII = 'rounded-tl-[20px] rounded-tr-[20px] rounded-bl-[4px] rounded-br-[20px]';

export const PeersonaMessageBubble: React.FC<Props> = ({ message, peerName }) => {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end pr-4">
        <div
          className={`max-w-[85%] ${USER_BUBBLE_RADII} px-5 py-3.5`}
          style={{ backgroundColor: PEERSONA_BUBBLE_USER_BG }}
        >
          <p
            className="font-open-sans whitespace-pre-wrap break-words text-sm leading-5"
            style={{ color: PEERSONA_BUBBLE_USER_TEXT }}
          >
            {message.content}
          </p>
        </div>
      </div>
    );
  }

  const referral = message.peersona?.referral;
  const safetyResources = message.peersona?.safety_resources;
  const handoff = message.peersona?.handoff;
  const tier = message.peersona?.tier;
  // Suppress the story tile on referral follow-ups — the assistant has
  // already paraphrased the peer's experience in prose.
  const showReferralContent = !message.streaming && referral && tier !== 'referral_followup';

  const compactTime = formatCompactRelativeTime({ iso: message.timestamp });

  return (
    <div className="flex justify-start pl-4">
      <div className="min-w-0 max-w-[85%] space-y-1">
        <div
          className={`${ASSISTANT_BUBBLE_RADII} px-5 py-3.5`}
          style={{ backgroundColor: PEERSONA_BUBBLE_ASSISTANT_BG }}
        >
          {message.content ? (
            <p
              className="font-open-sans whitespace-pre-wrap break-words text-sm leading-5"
              style={{ color: PEERSONA_BUBBLE_ASSISTANT_TEXT }}
            >
              {message.content}
              {message.streaming ? <span className="ml-1 inline-block animate-pulse text-white/70">▍</span> : null}
            </p>
          ) : message.streaming ? (
            <p className="font-open-sans text-sm italic leading-5 text-white/80">…</p>
          ) : (
            <p className="font-open-sans text-sm italic leading-5 text-white/70">No reply.</p>
          )}
        </div>
        {!message.streaming ? (
          <p className="font-albert-sans text-xs leading-5" style={{ color: PEERSONA_TEXT_MUTED }}>
            {peerName}
            <span aria-hidden>・</span>Peersona
            {compactTime ? (
              <>
                <span aria-hidden>・</span>
                {compactTime}
              </>
            ) : null}
          </p>
        ) : null}
        {safetyResources?.length ? <PeersonaSafetyCard resources={safetyResources} /> : null}
        {handoff ? <PeersonaHandoffCard handoff={handoff} /> : null}
        {showReferralContent ? <PeersonaReferralContent referral={referral} /> : null}
      </div>
    </div>
  );
};
